// app/src/Onboarding.jsx
// First-run onboarding wizard. Mounted by App.jsx when the signed-in user
// has no properties row, or one with onboarding_step !== 'done'.
//
// See docs/superpowers/specs/2026-05-10-onboarding-wizard-design.md
// Visual reference: docs/redesign/mockups/onboarding-wizard.html

import { useState, useEffect } from 'react';
import { supabase } from './supabase.js';
import { countryToCurrency, defaultLangForCountry, slugifyHotelName, wizardStepFromProperty } from './onboarding-utils.js';
import { resolveTypeLabel } from './room-types.js';
import { AddRoomModal } from './AddRoomModal.jsx';

// ── Brand atoms — match the live app (CL.* in App.jsx) ──────────────────────
const CL = {
  sand:  '#F5EFE6',
  cream: '#FFFDF8',
  bark:  '#3D2B1F',
  gold:  '#C9A96E',
  goldL: '#E8D5B0',
  olive: '#5A7247',
  terra: '#C0562B',
  stone: '#8B7D6B',
};
const FONT = {
  body:    "'Figtree', 'Helvetica Neue', -apple-system, sans-serif",
  display: '"Cormorant Garamond", Georgia, serif',
};

// ── Bilingual strings ───────────────────────────────────────────────────────
export const WIZARD_LANG = {
  en: {
    signOut: 'Sign out',
    back: '‹ Back',
    continue: 'Continue →',
    finish: 'Finish setup',
    saveErr: "Couldn't save — try again.",
    step1: {
      label: 'Step 1 of 3',
      title: 'Tell us about your hotel',
      sub:   'A few basics so we can set up your account.',
      name:  'Hotel name', namePh: 'e.g. Villa Marina',
      country: 'Country',
      address: 'Address', addressPh: 'Street, town, postcode',
      lang: 'Primary language', langEn: 'English', langBg: 'Bulgarian',
    },
    step2: {
      label: 'Step 2 of 3',
      title: 'Add your rooms',
      sub:   'You can add more or change these later in the Rooms tab.',
      addRoom: '+ Add room',
      empty:   'Tap "+ Add room" to add your first one. Most hotels start with 2–3 to get the feel.',
      hint:    'Add at least one room to continue.',
      sleeps:  'Capacity',
    },
    step3: {
      label: 'Step 3 of 3',
      title: 'Take direct bookings, skip the commission',
      sub:   'Booking.com keeps about 15% of every reservation. Your direct booking page lets guests book without that fee — and you keep the full nightly rate.',
      yes:   'Yes, set up my booking page',
      yesRecommend: 'Recommended',
      yesBody: 'Get a public link you can share with guests — WhatsApp, Instagram, Google listing. Works alongside Booking.com, just without their commission. You keep the full rate.',
      no:    'Not now',
      noBody: 'Keep using Booking.com as normal. You can add your direct booking page later from Settings — it takes 30 seconds.',
      slugLabel: 'Your booking page URL',
      slugAvailable: '✓ Available — you can change this later',
      slugTaken:  '✗ Taken — try',
    },
  },
  bg: {
    signOut: 'Изход',
    back: '‹ Назад',
    continue: 'Продължи →',
    finish: 'Завърши настройката',
    saveErr: 'Грешка при запазване. Опитайте пак.',
    step1: {
      label: 'Стъпка 1 от 3',
      title: 'Разкажете ни за хотела си',
      sub:   'Няколко основни данни за настройка на акаунта.',
      name:  'Име на хотела', namePh: 'напр. Villa Marina',
      country: 'Държава',
      address: 'Адрес', addressPh: 'Улица, град, пощенски код',
      lang: 'Основен език', langEn: 'Английски', langBg: 'Български',
    },
    step2: {
      label: 'Стъпка 2 от 3',
      title: 'Добави стаите си',
      sub:   'Можете да добавите още или да ги променяте по-късно в секция Стаи.',
      addRoom: '+ Добави стая',
      empty:   'Натиснете "+ Добави стая", за да добавите първата. Повечето хотели започват с 2–3.',
      hint:    'Добавете поне една стая, за да продължите.',
      sleeps:  'Капацитет',
    },
    step3: {
      label: 'Стъпка 3 от 3',
      title: 'Приемай директни резервации без комисионна',
      sub:   'Booking.com задържа около 15% от всяка резервация. Вашата директна страница позволява на гостите да резервират без тази такса и Вие запазвате цялата сума.',
      yes:   'Да, настройте страницата ми',
      yesRecommend: 'Препоръчано',
      yesBody: 'Получете публична връзка за споделяне с гости: WhatsApp, Instagram, Google. Работи заедно с Booking.com, само без тяхната комисионна. Вие запазвате пълната сума.',
      no:    'Не сега',
      noBody: 'Продължете с Booking.com нормално. Можете да добавите директната страница по-късно от Настройки. Отнема 30 секунди.',
      slugLabel: 'Адрес на Вашата страница',
      slugAvailable: '✓ Свободен, можете да го промените по-късно',
      slugTaken:  '✗ Зает, опитайте',
    },
  },
};

// ── Shared style atoms (mirror the live app's settings/login styling) ───────
const styles = {
  shell: { minHeight: '100svh', background: CL.sand, padding: '28px 22px 22px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', maxWidth: 480, margin: '0 auto', color: CL.bark },
  headerRow: { display: 'flex', alignItems: 'center', position: 'relative', marginBottom: 12, minHeight: 22 },
  brand:     { display: 'flex', alignItems: 'center', gap: 8, margin: '0 auto' },
  diamond:   { width: 8, height: 8, background: CL.gold, transform: 'rotate(45deg)' },
  brandName: { fontFamily: FONT.body, fontSize: 13, letterSpacing: '2.4px', fontWeight: 700, color: CL.bark, textTransform: 'uppercase' },
  rightCluster: { position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 12, alignItems: 'center' },
  langToggle:  { fontSize: 11, color: CL.stone, letterSpacing: '0.08em', cursor: 'pointer', background: 'none', border: 'none', padding: 0, fontFamily: FONT.body },
  langActive:  { color: CL.bark, fontWeight: 600 },
  signOut:     { fontSize: 11, color: CL.stone, letterSpacing: '0.08em', cursor: 'pointer', background: 'none', border: 'none', padding: 0, fontFamily: FONT.body },

  progressTrack: { height: 3, background: CL.goldL, margin: '18px -22px 26px', overflow: 'hidden' },
  progressFill:  (pct) => ({ width: `${pct}%`, height: '100%', background: CL.gold, transition: 'width 0.3s' }),

  stepNum: { fontSize: 11, color: CL.stone, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 },
  stepH:   { fontFamily: FONT.display, fontSize: 26, fontWeight: 500, margin: '0 0 6px', lineHeight: 1.1, color: CL.bark },
  stepSub: { fontSize: 13, color: CL.stone, margin: '0 0 22px', lineHeight: 1.4 },

  label:   { fontSize: 9.5, fontWeight: 700, color: CL.stone, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, display: 'block' },
  input:   (err) => ({ width: '100%', boxSizing: 'border-box', padding: '10px 12px', height: 40, border: `1.5px solid ${err ? CL.terra : CL.goldL}`, borderRadius: 8, background: CL.cream, fontSize: 14, color: CL.bark, marginBottom: 14, fontFamily: FONT.body, outline: 'none' }),

  pillRow: { display: 'flex', gap: 6, marginBottom: 14 },
  pill:    (selected) => ({ flex: 1, padding: '10px 8px', textAlign: 'center', border: `1.5px solid ${selected ? CL.bark : CL.goldL}`, borderRadius: 8, background: CL.cream, fontSize: 13, color: CL.bark, cursor: 'pointer', fontWeight: selected ? 600 : 400, fontFamily: FONT.body }),

  radioRow: { display: 'flex', gap: 16, marginBottom: 14 },
  radioLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: CL.bark, cursor: 'pointer' },

  primary: (disabled) => ({ width: '100%', background: CL.olive, color: CL.cream, border: 'none', borderRadius: 10, padding: 13, fontSize: 13, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', marginTop: 'auto', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1, fontFamily: FONT.body }),
  helper:  { fontSize: 11, color: CL.stone, textAlign: 'center', marginTop: 6 },
  errBox:  { fontSize: 12, color: CL.terra, textAlign: 'center', marginTop: 8 },
  backLink:{ background: 'none', border: 'none', padding: 0, color: CL.stone, fontSize: 12, cursor: 'pointer', marginTop: 14, marginBottom: 8, fontFamily: FONT.body, alignSelf: 'flex-start' },
};

// ── Header (TAVELO + lang toggle + Sign out) ────────────────────────────────
function WizardHeader({ lang, onToggleLang, onSignOut }) {
  return (
    <div style={styles.headerRow}>
      <div style={styles.brand}>
        <div style={styles.diamond}></div>
        <div style={styles.brandName}>TAVELO</div>
      </div>
      <div style={styles.rightCluster}>
        <button type="button" style={styles.langToggle} onClick={onToggleLang}>
          <span style={lang === 'en' ? styles.langActive : null}>EN</span>
          {' · '}
          <span style={lang === 'bg' ? styles.langActive : null}>BG</span>
        </button>
        <button type="button" style={styles.signOut} onClick={onSignOut}>
          {WIZARD_LANG[lang].signOut}
        </button>
      </div>
    </div>
  );
}

// ── Progress bar ────────────────────────────────────────────────────────────
function ProgressBar({ step }) {
  const pct = step === 'hotel' ? 33 : step === 'rooms' ? 66 : 100;
  return (
    <div style={styles.progressTrack}>
      <div style={styles.progressFill(pct)}></div>
    </div>
  );
}

// ── Step subcomponents — stubbed in this task; filled in Tasks 5–7 ──────────
function Step1Hotel({ userId, userEmail, property, lang, onAdvance }) {
  const t = WIZARD_LANG[lang].step1;

  // Pre-fill from existing row if user came back via Back from Step 2.
  const [name, setName]       = useState(property?.name ?? '');
  const [country, setCountry] = useState(property?.country ?? '');
  const [address, setAddress] = useState(property?.address ?? '');
  const [primaryLang, setPrimaryLang] = useState(
    (property?.lang ?? defaultLangForCountry(property?.country ?? '')).split(',')[0]
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  const canContinue = name.trim() && country && address.trim() && !saving;

  // When country changes, default-select the matching language radio
  // unless the user has already manually overridden.
  function pickCountry(c) {
    setCountry(c);
    setPrimaryLang(defaultLangForCountry(c));
  }

  async function onContinue() {
    if (!canContinue) return;
    setSaving(true);
    setErr('');
    const dbLang = country === 'GB' ? 'en' : primaryLang;
    const currency = countryToCurrency(country);

    const payload = {
      name: name.trim(),
      country,
      address: address.trim(),
      lang: dbLang,
      currency,
      onboarding_step: 'rooms',
    };

    let result;
    if (property?.id) {
      // Resume: row already exists from a prior Step 1 commit.
      result = await supabase
        .from('properties')
        .update(payload)
        .eq('id', property.id)
        .select('*')
        .single();
    } else {
      // First-time INSERT. owner_email is denormalised onto the row so the
      // admin panel (admin_customer_overview RPC) can show the owner without
      // joining auth.users; matches what admin_create_property writes.
      result = await supabase
        .from('properties')
        .insert({
          ...payload,
          owner_id: userId,
          owner_email: userEmail,
          message_template_enabled: {
            confirmation: false,
            deposit_request: true,
            balance_reminder: false,
            arrival_reminder: true,
            review_request: false,
          },
        })
        .select('*')
        .single();
    }

    setSaving(false);
    if (result.error) {
      console.error('Step 1 save failed:', result.error);
      setErr(WIZARD_LANG[lang].saveErr);
      return;
    }
    onAdvance(result.data);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={styles.stepNum}>{t.label}</div>
      <h2 style={styles.stepH}>{t.title}</h2>
      <p style={styles.stepSub}>{t.sub}</p>

      <label style={styles.label}>{t.name}</label>
      <input
        style={styles.input(false)}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t.namePh}
        autoFocus
      />

      <label style={styles.label}>{t.country}</label>
      <select
        value={country}
        onChange={(e) => pickCountry(e.target.value)}
        style={{ ...styles.input(false), marginBottom: 14, appearance: 'auto', height: 'auto', lineHeight: '1.4' }}
      >
        <option value="">— select —</option>
        <option value="GR">🇬🇷 Greece</option>
        <option value="BG">🇧🇬 Bulgaria</option>
        <option value="CY">🇨🇾 Cyprus</option>
        <option value="HR">🇭🇷 Croatia</option>
        <option value="ES">🇪🇸 Spain</option>
        <option value="PT">🇵🇹 Portugal</option>
        <option value="IT">🇮🇹 Italy</option>
        <option value="FR">🇫🇷 France</option>
        <option value="DE">🇩🇪 Germany</option>
        <option value="AT">🇦🇹 Austria</option>
        <option value="CH">🇨🇭 Switzerland</option>
        <option value="NL">🇳🇱 Netherlands</option>
        <option value="BE">🇧🇪 Belgium</option>
        <option value="GB">🇬🇧 United Kingdom</option>
        <option value="IE">🇮🇪 Ireland</option>
        <option value="US">🇺🇸 United States</option>
        <option value="AU">🇦🇺 Australia</option>
      </select>

      <label style={styles.label}>{t.address}</label>
      <input
        style={styles.input(false)}
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder={t.addressPh}
      />

      {country !== 'GB' && country !== '' && (
        <>
          <label style={styles.label}>{t.lang}</label>
          <div style={styles.radioRow}>
            <label style={styles.radioLabel}>
              <input type="radio" name="primaryLang" checked={primaryLang === 'en'} onChange={() => setPrimaryLang('en')} style={{ accentColor: CL.olive }} />
              {t.langEn}
            </label>
            <label style={styles.radioLabel}>
              <input type="radio" name="primaryLang" checked={primaryLang === 'bg'} onChange={() => setPrimaryLang('bg')} style={{ accentColor: CL.olive }} />
              {t.langBg}
            </label>
          </div>
        </>
      )}

      <button
        type="button"
        style={styles.primary(!canContinue)}
        disabled={!canContinue}
        onClick={onContinue}
      >
        {WIZARD_LANG[lang].continue}
      </button>
      {err && <div style={styles.errBox}>{err}</div>}
    </div>
  );
}
function Step2Rooms({ property, lang, onBack, onAdvance }) {
  const t = WIZARD_LANG[lang].step2;
  const cur = property?.currency === 'GBP' ? '£' : '€';

  const [rooms, setRooms]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modalRoom, setModalRoom] = useState(null); // {} for create, room object for edit, null for closed
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  // Initial fetch + after each modal save.
  async function refetch() {
    const { data, error } = await supabase
      .from('rooms')
      .select('*')
      .eq('property_id', property.id)
      .order('sort_order');
    if (!error) setRooms(data || []);
    setLoading(false);
  }

  useEffect(() => { refetch(); }, [property.id]);

  async function onModalSubmit(form) {
    if (modalRoom?.id) {
      // Edit — update existing
      const { error } = await supabase
        .from('rooms')
        .update({
          name: form.name, type: form.type, capacity: form.capacity,
          max_adults: form.maxAdults, max_children: form.maxChildren,
          base_price: form.basePrice,
        })
        .eq('id', modalRoom.id);
      if (error) { setErr(WIZARD_LANG[lang].saveErr); return; }
    } else {
      // Create — insert with sort_order = max + 1, seasons = []
      const nextOrder = (rooms.length === 0 ? 1 : Math.max(...rooms.map(r => r.sort_order || 0)) + 1);
      const { error } = await supabase.from('rooms').insert({
        property_id: property.id,
        name: form.name, type: form.type, capacity: form.capacity,
        max_adults: form.maxAdults, max_children: form.maxChildren,
        base_price: form.basePrice,
        seasons: [],
        sort_order: nextOrder,
        archived: false,
      });
      if (error) { setErr(WIZARD_LANG[lang].saveErr); return; }
    }
    setModalRoom(null);
    setErr('');
    await refetch();
  }

  async function onContinue() {
    if (rooms.length === 0 || saving) return;
    setSaving(true);
    setErr('');
    const { data, error } = await supabase
      .from('properties')
      .update({ onboarding_step: 'booking_page' })
      .eq('id', property.id)
      .select('*')
      .single();
    setSaving(false);
    if (error) { setErr(WIZARD_LANG[lang].saveErr); return; }
    onAdvance(data);
  }

  const canContinue = rooms.length > 0 && !saving;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={styles.stepNum}>{t.label}</div>
      <h2 style={styles.stepH}>{t.title}</h2>
      <p style={styles.stepSub}>{t.sub}</p>

      {loading ? (
        <div style={{ color: CL.stone, textAlign: 'center', padding: 20 }}>…</div>
      ) : rooms.length === 0 ? (
        <div style={{ textAlign: 'center', fontSize: 12, color: CL.stone, padding: '20px 12px', marginBottom: 14, lineHeight: 1.5 }}>
          {t.empty}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {rooms.map(r => (
            <div
              key={r.id}
              onClick={() => setModalRoom(r)}
              style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', border: `1px solid ${CL.goldL}`, borderRadius: 8, background: CL.cream, fontSize: 13, cursor: 'pointer' }}
            >
              <div style={{ fontWeight: 600 }}>{r.name}</div>
              <div style={{ color: CL.stone, marginLeft: 'auto' }}>
                {resolveTypeLabel(r.type, property?.room_type_info, lang)} · {t.sleeps} {r.capacity} · {cur}{r.base_price}
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setModalRoom({})}
        style={{ width: '100%', padding: 12, border: `1.5px dashed ${CL.gold}`, borderRadius: 8, background: 'transparent', color: CL.gold, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 14, fontFamily: FONT.body }}
      >
        {t.addRoom}
      </button>

      {!canContinue && rooms.length === 0 && (
        <div style={styles.helper}>{t.hint}</div>
      )}

      <button type="button" style={styles.backLink} onClick={onBack}>
        {WIZARD_LANG[lang].back}
      </button>

      <button
        type="button"
        style={styles.primary(!canContinue)}
        disabled={!canContinue}
        onClick={onContinue}
      >
        {WIZARD_LANG[lang].continue}
      </button>
      {err && <div style={styles.errBox}>{err}</div>}

      {modalRoom !== null && (
        <AddRoomModal
          property={property}
          lang={lang}
          onCancel={() => setModalRoom(null)}
          onSubmit={onModalSubmit}
          onUpdateRoomTypeInfo={() => {}}
        />
      )}
    </div>
  );
}
function Step3BookingPage({ property, lang, onBack, onComplete }) {
  const t = WIZARD_LANG[lang].step3;

  const [choice, setChoice] = useState(null); // 'yes' | 'no' | null
  const [slug, setSlug] = useState(slugifyHotelName(property?.name || ''));
  const [slugStatus, setSlugStatus] = useState({ state: 'idle', suggested: null });
  // state: 'idle' | 'checking' | 'available' | 'taken'
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Live uniqueness check on slug blur or change (debounced).
  useEffect(() => {
    if (choice !== 'yes' || !slug) return;
    const slugTrimmed = slug.trim();
    if (!slugTrimmed) return;

    let cancelled = false;
    setSlugStatus({ state: 'checking', suggested: null });
    const timer = setTimeout(async () => {
      const { data: available, error } = await supabase
        .rpc('is_slug_available', { p_slug: slugTrimmed, p_exclude_id: property.id });
      if (cancelled) return;
      if (error) { setSlugStatus({ state: 'idle', suggested: null }); return; }
      if (available) {
        setSlugStatus({ state: 'available', suggested: null });
      } else {
        // Find next available suffix via RPC
        const base = slugTrimmed.replace(/-\d+$/, '');
        let n = 2;
        while (n < 50) {
          const candidate = `${base}-${n}`;
          const { data: candidateAvail } = await supabase
            .rpc('is_slug_available', { p_slug: candidate, p_exclude_id: property.id });
          if (cancelled) return;
          if (candidateAvail) {
            setSlugStatus({ state: 'taken', suggested: candidate });
            return;
          }
          n++;
        }
        setSlugStatus({ state: 'taken', suggested: `${base}-${Date.now()}` });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [slug, choice, property.id]);

  async function onFinish() {
    if (saving || choice === null) return;
    setSaving(true);
    setErr('');

    let finalSlug;
    let hasBookingPage;

    if (choice === 'yes') {
      finalSlug = slug.trim();
      hasBookingPage = true;
      if (slugStatus.state === 'taken') {
        finalSlug = slugStatus.suggested;
      }
    } else {
      // No-path: silently auto-compute slug with collision suffix
      const base = slugifyHotelName(property.name);
      let candidate = base;
      let found = false;
      let n = 2;
      while (n < 50) {
        const { data: avail } = await supabase
          .rpc('is_slug_available', { p_slug: candidate, p_exclude_id: property.id });
        if (avail) { found = true; break; }
        candidate = `${base}-${n}`;
        n++;
      }
      finalSlug = found ? candidate : `${base}-${Date.now()}`;
      hasBookingPage = false;
    }

    const { data, error } = await supabase
      .from('properties')
      .update({
        slug: finalSlug,
        has_booking_page: hasBookingPage,
        onboarding_step: 'done',
      })
      .eq('id', property.id)
      .select('*')
      .single();

    setSaving(false);
    if (error) {
      console.error('Step 3 save failed:', error);
      setErr(WIZARD_LANG[lang].saveErr);
      return;
    }
    onComplete(data);
  }

  const canFinish = choice !== null && !saving &&
    (choice === 'no' || (choice === 'yes' && slug.trim() && slugStatus.state !== 'checking'));

  const choiceCardStyle = (selected) => ({
    padding: 16,
    border: `1.5px solid ${selected ? CL.olive : CL.goldL}`,
    borderRadius: 12,
    background: selected ? '#f5f8f3' : CL.cream,
    marginBottom: 12,
    cursor: 'pointer',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={styles.stepNum}>{t.label}</div>
      <h2 style={styles.stepH}>{t.title}</h2>
      <p style={styles.stepSub}>{t.sub}</p>

      <div style={choiceCardStyle(choice === 'yes')} onClick={() => setChoice('yes')}>
        <div style={{ fontWeight: 600, fontSize: 15, color: CL.bark, marginBottom: 4 }}>
          ✓ {t.yes}
          <span style={{ fontSize: 10, color: CL.olive, textTransform: 'uppercase', letterSpacing: 1, marginLeft: 6 }}>
            {t.yesRecommend}
          </span>
        </div>
        <div style={{ fontSize: 12, color: CL.stone, lineHeight: 1.5 }}>{t.yesBody}</div>

        {choice === 'yes' && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${CL.goldL}` }}>
            <label style={styles.label}>{t.slugLabel}</label>
            <div style={{ fontFamily: 'SF Mono, Menlo, monospace', fontSize: 13, color: CL.bark, padding: '10px 12px', background: CL.sand, borderRadius: 6, marginBottom: 8 }}>
              book.tavelo.app/
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                style={{ background: 'transparent', border: 'none', fontFamily: 'SF Mono, Menlo, monospace', fontSize: 13, color: CL.bark, padding: 0, width: 160, outline: 'none', fontWeight: 700 }}
              />
            </div>
            {slugStatus.state === 'available' && (
              <div style={{ fontSize: 11, color: CL.olive, textAlign: 'left' }}>{t.slugAvailable}</div>
            )}
            {slugStatus.state === 'taken' && (
              <div style={{ fontSize: 11, color: CL.terra, textAlign: 'left' }}>
                {t.slugTaken} <button type="button" onClick={() => setSlug(slugStatus.suggested)} style={{ background: 'none', border: 'none', color: CL.gold, cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>{slugStatus.suggested}</button>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={choiceCardStyle(choice === 'no')} onClick={() => setChoice('no')}>
        <div style={{ fontWeight: 600, fontSize: 15, color: CL.bark, marginBottom: 4 }}>{t.no}</div>
        <div style={{ fontSize: 12, color: CL.stone, lineHeight: 1.5 }}>{t.noBody}</div>
      </div>

      <button type="button" style={styles.backLink} onClick={onBack}>
        {WIZARD_LANG[lang].back}
      </button>

      <button
        type="button"
        style={styles.primary(!canFinish)}
        disabled={!canFinish}
        onClick={onFinish}
      >
        {WIZARD_LANG[lang].finish}
      </button>
      {err && <div style={styles.errBox}>{err}</div>}
    </div>
  );
}

// ── Default export — orchestrator ───────────────────────────────────────────
// Receives the current property (or null for fresh sign-in). Renders the
// step matching wizardStepFromProperty(property). Reports progress back via
// onPropertyChange so App.jsx's state stays in sync.
export default function OnboardingWizard({ userId, userEmail, property, onPropertyChange }) {
  const initialStep = wizardStepFromProperty(property) || 'hotel';
  const [step, setStep] = useState(initialStep);
  const [lang, setLang] = useState('en');

  function toggleLang() {
    setLang((l) => (l === 'en' ? 'bg' : 'en'));
  }
  function onSignOut() {
    supabase.auth.signOut();
  }

  return (
    <div style={styles.shell}>
      <WizardHeader lang={lang} onToggleLang={toggleLang} onSignOut={onSignOut} />
      <ProgressBar step={step} />
      {step === 'hotel' && (
        <Step1Hotel
          userId={userId}
          userEmail={userEmail}
          property={property}
          lang={lang}
          onAdvance={(nextProperty) => { onPropertyChange(nextProperty); setStep('rooms'); }}
        />
      )}
      {step === 'rooms' && (
        <Step2Rooms
          property={property}
          lang={lang}
          onBack={() => setStep('hotel')}
          onAdvance={(nextProperty) => { onPropertyChange(nextProperty); setStep('booking_page'); }}
        />
      )}
      {step === 'booking_page' && (
        <Step3BookingPage
          property={property}
          lang={lang}
          onBack={() => setStep('rooms')}
          onComplete={(nextProperty) => onPropertyChange(nextProperty)}
        />
      )}
    </div>
  );
}
